import type { Chat } from '@ai-sdk/vue'
import type {
    Chat as Conversation,
    MessageFeedback,
    UIChatMessage,
} from 'models'
import type { ComputedRef, InjectionKey, Ref } from 'vue'
import type { AgentInterface, ChatMessageActions, RevisedAnswer } from 'models'
import { LanguageModel } from 'models/src/schema/constants'

export interface ChatbotContext {
    api: string
    agentId: Ref<string>
    name: Ref<string | undefined>
    agentInterface: Ref<AgentInterface | undefined>
    actions: Ref<ChatMessageActions[] | undefined>
    revisedAnswers: Ref<RevisedAnswer[] | undefined>
    headers: Ref<Record<string, string> | undefined>
    messages: ComputedRef<UIChatMessage[]>
    inputMessagePlaceholder: ComputedRef<string>
    chat: ComputedRef<Chat<UIChatMessage>>
    conversations: Ref<Conversation[]>
    input: Ref<string>
    localChatId: Ref<string>
    handleSubmit: (e?: Event) => void
    stopGeneration: () => Promise<void>
    addToolOutput: (params: {
        tool: string
        toolCallId: string
        output: unknown
    }) => void
    startNewChat: () => Promise<string>
    regenerate: (
        params?: Parameters<Chat<UIChatMessage>['regenerate']>[0],
    ) => Promise<void>
    isLeadSubmitted: Ref<boolean>
    isLoadingSubmitLead: Ref<boolean>
    submitLeadError: Ref<string | undefined>
    onLeadSubmit: (data: Record<string, unknown>) => Promise<void>
    navigate: (view: 'chat' | 'conversations' | 'profile') => void
    messageFeedbacks: Ref<MessageFeedback[]>
    feedbackDialogMessage: Ref<UIChatMessage | undefined>
    isFeedbackSubmitting: Ref<boolean>
    isFeedbackSubmitted: Ref<boolean>
    feedbackSubmitError: Ref<string | undefined>
    onUpvote: (message: UIChatMessage) => Promise<void>
    onDownvote: (message: UIChatMessage) => Promise<void>
    onFeedback: (message: UIChatMessage) => void
    onFeedbackSubmit: (comment: string) => Promise<void>
    emit: ((event: 'show-info', message: UIChatMessage) => void) &
        ((event: 'revise', message: UIChatMessage) => void)
}

export const chatbotContextKey: InjectionKey<ChatbotContext> =
    Symbol('chatbotContext')

export const MODELS = [
    LanguageModel.gpt5Mini,
    LanguageModel.gpt5Nano,
    LanguageModel.gpt51Instant,
    LanguageModel.gpt51Thinking,
    LanguageModel.gpt52,
    LanguageModel.gpt52Chat,
    LanguageModel.gemini25Flash,
    LanguageModel.gemini25FlashLite,
    LanguageModel.gemini25Pro,
    LanguageModel.gemini3Flash,
    LanguageModel.gemini3ProPreview,
    LanguageModel.gemini31ProPreview,
    LanguageModel.gemini31FlashLitePreview,
    LanguageModel.claudeHaiku45,
    LanguageModel.claudeSonnet46,
    LanguageModel.claudeOpus46,
    LanguageModel.deepseek32,
    LanguageModel.deepseek32Thinking,
] as const

export const DEFAULT_TEMPERATURE = 0.6
export const DEFAULT_PROMPT = ''
export const DEFAULT_LIMIT = 5
